import React, { memo, useCallback, useEffect, useState } from "react";
import AddClientModal from "../../app/components/add-client-modal/AddClientModal";
import EmptyPage from "../../app/components/empty-page/EmptyPage";
import PaginationCustom from "../../app/components/pagination/PaginationCustom";
import HeaderNew from "../../app/components/redesign/Header/HeaderNew";
import SortingLine from "../../app/components/sorting-line/SortingLine";
import { COLORS } from "../../core/consts/redesign/commonRedesign";
import { Plus } from "../../core/consts/redesign/icons";
import useIsMobile from "../../core/hooks/useIsMobile";
import agentService from "../../core/services/AgentService";
import { AgentClients, AgentClientsListType, ClientTypes, clientTypesEnumMap, GetClientsQueryType } from "../../core/types/agentTypes";
import ButtonCustom from "../../UIcomponentsRedesign/ButtonCustom/ButtonCustom";
import IconButtonMain from "../../UIcomponentsRedesign/IconButtonMain/IconButtonMain";
import SpinnerMain from "../../UIcomponentsRedesign/SpinnerMain";
import ClientCard from "./components/client-card/ClientCard";
import ClientsFilter from "./components/clients-filter/ClientsFilter";
import { useStylesAgentClients } from "./useStylesAgentClients";

/* Redesign . All agent's clients page. */

const AgentClientsPage: React.FC = () => {
  const classes = useStylesAgentClients();
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState({ offset: 0, count: 0, total: 0 });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [openAddClientModal, setOpenAddClientModal] = useState<boolean>(false);
  const [searchInn, setSearchInn] = useState<string>('');
  const [searchCompanyName, setSearchCompanyName] = useState<string>('');
  const [searchRegion, setSearchRegion] = useState<string>('');
  const [searchClientType, setSearchClientType] = useState<string>('')
  const [filters, setFilters] = useState({
    innList: [] as string[],
    companyNameList: [] as string[],
    regionsList: [] as string[],
    clientTypeList: [...Object.values(ClientTypes)] as string[],
  });
  const [text, setText] = useState('');
  const [clientsData, setClientsData] = useState<AgentClientsListType>();
  const [clients, setClients] = useState<AgentClients[]>([]);

  const totalClients = pagination.total;
  const clientsPerPage = 16;
  const getClientsRequest = useCallback(() => {
    let Q: GetClientsQueryType = {
      Inn: searchInn,
      Region: searchRegion === '' ? '' : searchRegion,
      CompanyName: searchCompanyName,
      ClientType: searchClientType === '' ? '' : clientTypesEnumMap[searchClientType as ClientTypes],
      offset: (currentPage - 1) * clientsPerPage,
      count: clientsPerPage,
    };
    agentService.getClientsListService(Q)
      .then((res: AgentClientsListType | 'Ошибка') => {
        if (res === 'Ошибка') {
          setClientsData(undefined)
          setText('Произошла ошибка, попробуйте перезагрузить страницу.')
        } else {
          setClientsData(res);
          setClients(res.clients)
          setFilters(prevFilters => ({
            ...prevFilters,
            innList: res.inns || [],
            companyNameList: res.companyNames || [],
            regionsList: res.regions || [],
          }));
          setPagination(res.pagination || { offset: 0, count: 0, total: 0 });
        }
        setIsLoading(false);
      })
      .catch((error: any) => {
        console.log(error);
        setIsLoading(false);
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [
    searchInn,
    searchRegion,
    searchClientType,
    searchCompanyName,
    currentPage
  ])

  useEffect(() => {
    const delay = 500;
    const timerId = setTimeout(() => {
      getClientsRequest();
    }, delay);
    return () => clearTimeout(timerId);
  }, [getClientsRequest]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentPage]);

  const paginate = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleClearFilters = useCallback(() => {
    setCurrentPage(1);
    setSearchCompanyName('');
    setSearchInn('');
    setSearchRegion('');
    setSearchClientType('')
    getClientsRequest();
  }, [getClientsRequest]);

  const handleClearInn = useCallback(() => {
    setCurrentPage(1);
    setSearchInn('');
    getClientsRequest();
  }, [getClientsRequest]);

  const handleClearCompanyName = useCallback(() => {
    setCurrentPage(1);
    setSearchCompanyName('');
    getClientsRequest();
  }, [getClientsRequest]);

  return (
    <div>
      <HeaderNew title={"Клиенты"}>
        {isMobile ?
          <IconButtonMain
            color="blue"
            style={{ marginRight: '8px' }}
            handleClick={() => setOpenAddClientModal(true)}
            icon={<Plus />} />
          :
          <ButtonCustom
            style={{ position: 'relative' }}
            color="primary"
            handleClick={() => setOpenAddClientModal(true)}>
            Добавить клиента
          </ButtonCustom>}
      </HeaderNew>
      <div className={classes.container}>
        <div className={classes.content}>
          {isLoading ?
            <SpinnerMain />
            :
            <>
              <ClientsFilter
                innList={filters.innList}
                companyNameList={filters.companyNameList}
                regionsList={filters.regionsList}
                setSearchInn={setSearchInn}
                searchInn={searchInn}
                setSearchCompanyName={setSearchCompanyName}
                searchCompanyName={searchCompanyName}
                searchClientType={searchClientType}
                setSearchClientType={setSearchClientType}
                searchRegion={searchRegion}
                setSearchRegion={setSearchRegion}
                onClear={handleClearFilters}
                onClearCompanyName={handleClearCompanyName}
                onClearInn={handleClearInn}
                setCurrentPage={setCurrentPage}
              />
              <SortingLine
                items={clientsData}
                totalItems={totalClients}
              />

              {clients.length > 0 &&
                <>
                  {clients.map((client: AgentClients, index: number) => (
                    <ClientCard clientInfo={client} key={index} />
                  ))}
                  <PaginationCustom
                    currentPage={currentPage}
                    itemsPerPage={clientsPerPage}
                    totalItems={totalClients}
                    onPageChange={paginate}
                  />
                </>
              }
              {!clientsData && (text !== "") &&
                <EmptyPage
                  titleColor={COLORS.gray2}
                  title='Здесь будут находиться ваши клиенты'
                  content="Вы можете добавить клиента на этом экране"
                  button={
                    <ButtonCustom color="primary" handleClick={() => setOpenAddClientModal(true)}>
                      Новая заявка
                    </ButtonCustom>
                  }
                />}

              {openAddClientModal &&
                <AddClientModal
                  isOpen={openAddClientModal}
                  setCloseModal={() => setOpenAddClientModal(false)}
                />
              }
            </>
          }
        </div>
      </div>
    </div>
  )
}

export default memo(AgentClientsPage)